import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faShieldCheck } from '@fortawesome/pro-solid-svg-icons/faShieldCheck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { resolveHealthCheckScorePresentation } from '../Onboarding/ContentInventory/Components/HealthCheckScorePresentation';

/** Neutral gray badge styling used when no confidence/certainty is available. */
const NEUTRAL_BADGE_STYLE = { backgroundColor: 'rgb(241, 245, 249)', color: 'rgb(100, 116, 139)' };

/** Non-interactive shield-check + percentage badge, colored by confidence. */
export function ShieldConfidenceBadge({ confidence, title }: { confidence: number | null; title?: string }) {
  if (confidence === null) {
    return (
      <span className="inline-flex items-center text-sm font-semibold text-slate-400" title={title}>
        —
      </span>
    );
  }

  const presentation = resolveHealthCheckScorePresentation(confidence);

  return (
    <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold" style={presentation.style} title={title}>
      <FontAwesomeIcon icon={faShieldCheck} className="text-xs" />
      {confidence}%
    </span>
  );
}

/**
 * Interactive confidence badge shown after the gap name. Toggles the inline detail
 * panel. A `null` confidence renders a neutral gray dash (no fabricated percentage).
 *
 * The hover hint uses the native `title` attribute on purpose: the previous
 * react-bootstrap `OverlayTrigger`/`Tooltip` rendered a popper into a portal that
 * mispositioned (and showed a stray arrow) inside the resized iframe.
 */
export function GapConfidenceBadge({
  confidence,
  expanded,
  controlsId,
  onToggle,
  tooltip,
  label = 'Show assignment details',
}: {
  confidence: number | null;
  expanded: boolean;
  controlsId: string;
  onToggle: () => void;
  tooltip?: string | null;
  label?: string;
}) {
  const hasConfidence = typeof confidence === 'number' && Number.isFinite(confidence);
  const style = hasConfidence ? resolveHealthCheckScorePresentation(confidence).style : NEUTRAL_BADGE_STYLE;
  const confidenceLabel = hasConfidence ? `${confidence}%` : '—';
  const ariaLabel = hasConfidence ? `${label} (${confidence}% confidence)` : `${label} (confidence unavailable)`;

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-controls={controlsId}
      aria-label={ariaLabel}
      title={tooltip || undefined}
      className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      style={style}
    >
      <FontAwesomeIcon icon={faShieldCheck} className="text-xs" />
      <span>{confidenceLabel}</span>
      <FontAwesomeIcon
        icon={faChevronDown}
        className="text-[0.6rem] transition-transform"
        style={{ transform: expanded ? 'rotate(180deg)' : undefined }}
      />
    </button>
  );
}
